/**
 * VCB — sonda degli errori del sito.
 *
 * Registra in GA4 tre cose che oggi non sappiamo:
 *   vcb_js_error      errori JavaScript e promesse rifiutate
 *   vcb_page_crash    la pagina precedente e' morta senza chiudersi (renderer OOM)
 *   vcb_empty_results collezione o ricerca che non mostra nessun prodotto
 *
 * Perche' il crash si rileva DOPO e non mentre accade: quando il renderer
 * esaurisce la memoria il processo viene ucciso, e con lui il JavaScript che
 * dovrebbe segnalarlo. L'unico modo e' lasciare un segnale all'avvio e
 * cancellarlo su `pagehide` (che scatta a ogni uscita pulita, navigazione o
 * bfcache). Se al caricamento dopo il segnale e' ancora li', la pagina prima e'
 * morta male.
 *
 * ⚠️ Falsi positivi possibili: chiusura forzata del browser, spegnimento,
 * scheda uccisa dal sistema su mobile. Serve a contare e a capire DOVE, non a
 * dimostrare il singolo caso.
 *
 * Privacy: nessun dato personale. Si manda il PERCORSO senza query string (che
 * puo' contenere termini di ricerca), il messaggio d'errore troncato, e nulla
 * che identifichi la persona.
 *
 * Robustezza: tutto dentro try/catch e con un tetto di eventi per pagina. Una
 * sonda che rompe il sito o inonda GA4 e' peggio di nessuna sonda.
 */
(function () {
  'use strict';

  var CHIAVE = 'vcb_nav';
  // Tetto PER TIPO di evento, non complessivo. Con un tetto unico i tre
  // valori di velocità (che partono sempre) esaurirebbero il budget e gli
  // errori non arriverebbero mai — proprio quelli che ci interessano di più.
  var TETTI = {
    vcb_js_error: 8,      // un loop di errori non deve inondare
    vcb_page_crash: 1,
    vcb_empty_results: 1,
    vcb_web_vitals: 3,    // lcp, cls, inp: uno ciascuno
    vcb_rage_click: 2,
    vcb_taglia_esaurita: 4,   // quante taglie prova, non quante volte
    vcb_click_morto: 2
  };
  var contati = {};

  function percorso() {
    try {
      return (location.pathname || '/').slice(0, 120); // senza query: puo' contenere ricerche
    } catch (e) {
      return '/';
    }
  }

  /**
   * Ultimo segmento di una URL, SENZA query string.
   * ⚠️ La query va tolta anche qui, non solo da `page_path`: un errore dentro
   * uno script inline ha come `filename` la URL della pagina, quindi su
   * /search?q=... finirebbe in GA4 il termine cercato dall'utente.
   * Trovato provando la sonda il 21/08/2026.
   */
  function senzaQuery(u) {
    try {
      return String(u || '').split('?')[0].split('#')[0].split('/').pop().slice(0, 60);
    } catch (e) {
      return '';
    }
  }

  /**
   * Da quale risorsa arriva un errore di caricamento.
   *
   * ⚠️ `senzaQuery` da sola non basta qui, e per due ragioni misurate il
   * 27/08/2026 su 2.095 errori di tipo SCRIPT:
   *
   *   1. il 71% arrivava con sorgente VUOTA. Un `<script>` che fallisce puo'
   *      avere `src` non ancora valorizzato (iniettato e rimosso), e restava
   *      una riga che diceva solo "uno script e' fallito" — inutile.
   *   2. il solo nome del file perde il DOMINIO, che e' il pezzo che conta:
   *      `js` puo' essere di chiunque, `connect.facebook.net/js` no. Quasi
   *      tutte queste risorse sono di terze parti, e sapere di CHI e' il
   *      problema decide se possiamo farci qualcosa.
   *
   * Quando la URL manca si ripiega sull'identita' dell'elemento: id, classe,
   * o il tipo. Meglio "script[async] senza src" che il nulla.
   */
  function origineRisorsa(t) {
    try {
      var u = t.src || t.href || t.currentSrc || '';
      if (u) {
        // ⚠️ Il confronto va fatto su host NORMALIZZATI da entrambi i lati:
        // togliere `www.` solo a uno dei due fa risultare estranee anche le
        // nostre risorse, e ogni riga sprecherebbe 17 caratteri per dire
        // "e' nostra".
        var nudo = function (h) { return String(h || '').replace(/^www\./, ''); };
        var host = '';
        try {
          host = nudo(new URL(u, location.href).host);
        } catch (e) {}
        var f = senzaQuery(u) || '(radice)';
        return (host && host !== nudo(location.host) ? host + '/' : '') + f;
      }
      var id = t.id ? '#' + t.id : '';
      var cls = t.className && typeof t.className === 'string'
        ? '.' + t.className.split(/\s+/)[0] : '';
      return ('senza-url ' + String(t.tagName).toLowerCase() + id + cls).slice(0, 60);
    } catch (e) {
      return '';
    }
  }

  // Coda: i guasti capitano anche prima che gtag.js sia pronto (anzi, il crash
  // capita proprio all'inizio). Qui si accumulano e si spurgano appena arriva.
  var coda = [];

  function spurga() {
    if (typeof window.gtag !== 'function') return false;
    while (coda.length) {
      var ev = coda.shift();
      try { window.gtag('event', ev[0], ev[1]); } catch (e) { /* ignora */ }
    }
    return true;
  }

  function invia(nome, dati) {
    try {
      var tetto = TETTI[nome] || 2;
      contati[nome] = (contati[nome] || 0) + 1;
      if (contati[nome] > tetto) return;
      dati = dati || {};
      dati.page_path = percorso();
      coda.push([nome, dati]);

      // ⚠️ NON usare dataLayer.push(['event', ...]).
      // Su questo negozio GA4 e' alimentato dai Web Pixels di Shopify e gtag.js
      // arriva dinamicamente: un push in quel formato NON produce alcuna
      // richiesta di rete. Verificato il 25/08/2026 sparando i tre metodi e
      // guardando il traffico:
      //   gtag('event', ...)            -> POST a /g/collect con tid=G-V2BZMC4G4H
      //   dataLayer.push(['event',...]) -> niente
      //   Shopify.analytics.publish()   -> niente verso GA4
      // Per questo la sonda ha taciuto per 28 giorni, crash compreso.
      if (spurga()) return;

      // gtag non c'e' ancora: riprova a intervalli, poi si arrende.
      var tentativi = 0;
      var t = setInterval(function () {
        if (spurga() || ++tentativi > 40) clearInterval(t);
      }, 250);
    } catch (e) {
      /* la sonda non deve mai far cadere la pagina */
    }
  }

  /* Rumore atteso, non guasti.
     Dopo OGNI pubblicazione del tema gli identificativi delle sezioni cambiano.
     Una pagina aperta prima della pubblicazione, quando prova a idratare il
     terzo livello del menu, chiede una sezione che non esiste piu' e
     `section-renderer.js` solleva un errore. Non e' un difetto: e' previsto,
     l'utente vede semplicemente il menu a due livelli.
     Misurato il 04/09/2026: 2.522 + 2.391 errori in un giorno con quattro
     pubblicazioni, contro 14 in un giorno senza. Contarli sporca la misura e
     nasconde i guasti veri.
     ⚠️ Se un giorno questi errori comparissero SENZA una pubblicazione,
     vorrebbe dire che il render delle sezioni e' rotto davvero: togliere
     questo filtro e guardare. */
  function attesoDopoPubblicazione(msg) {
    if (!msg) return false;
    return (msg.indexOf('Section ') === 0 && msg.indexOf('not found') > 0) ||
           msg.indexOf('No empty section markup found') === 0;
  }

  /* ---------------------------------------------- 1. errori JavaScript */
  window.addEventListener('error', function (ev) {
    try {
      if (!ev) return;
      // Gli errori di caricamento risorsa (img/script) non hanno `message`:
      // sono rumore diverso, li marchiamo a parte.
      if (!ev.message && ev.target && ev.target.tagName) {
        invia('vcb_js_error', {
          err_tipo: 'risorsa',
          err_msg: String(ev.target.tagName).slice(0, 20),
          err_src: origineRisorsa(ev.target)
        });
        return;
      }
      var m = String(ev.message || '');
      if (attesoDopoPubblicazione(m)) return;
      invia('vcb_js_error', {
        err_tipo: 'script',
        err_msg: m.slice(0, 100),
        err_src: senzaQuery(ev.filename) + ':' + (ev.lineno || 0)
      });
    } catch (e) {}
  }, true);

  window.addEventListener('unhandledrejection', function (ev) {
    try {
      var r = ev && ev.reason;
      var mp = String((r && (r.message || r)) || '');
      if (attesoDopoPubblicazione(mp)) return;
      invia('vcb_js_error', {
        err_tipo: 'promessa',
        err_msg: mp.slice(0, 100),
        err_src: ''
      });
    } catch (e) {}
  });

  /* ------------------------------------- 2. la pagina prima e' morta? */
  try {
    var prec = sessionStorage.getItem(CHIAVE);
    if (prec) {
      var p = JSON.parse(prec);
      // Se il segnale e' ancora qui, `pagehide` non e' mai scattato.
      invia('vcb_page_crash', {
        crash_page: String(p.p || '').slice(0, 120),
        crash_durata_s: p.t ? Math.round((Date.now() - p.t) / 1000) : 0
      });
    }
    sessionStorage.setItem(CHIAVE, JSON.stringify({ p: percorso(), t: Date.now() }));
    window.addEventListener('pagehide', function () {
      try {
        sessionStorage.removeItem(CHIAVE);
      } catch (e) {}
    });
  } catch (e) {
    /* sessionStorage puo' essere negato (navigazione privata, cookie bloccati) */
  }

  /* --------------------------------------- 3. pagine senza risultati */
  window.addEventListener('load', function () {
    try {
      var p = percorso();
      if (p.indexOf('/collections/') !== 0 && p.indexOf('/search') !== 0) return;
      // Si guarda la griglia dei risultati, non tutta la pagina: i caroselli
      // di raccomandazione contengono prodotti anche quando la griglia e' vuota.
      var griglia = document.querySelector('#ResultsList, .main-collection-grid');
      if (!griglia) return;
      if (griglia.querySelectorAll('a[href*="/products/"]').length === 0) {
        invia('vcb_empty_results', { tipo: p.indexOf('/search') === 0 ? 'ricerca' : 'collezione' });
      }
    } catch (e) {}
  });

  // ------------------------------------------------------------------
  // VELOCITÀ REALE e INCEPPI
  //
  // Fino a oggi non avevamo NESSUN dato di velocità percepita dagli utenti
  // veri: PageSpeed richiede una chiave, il browser automatizzato non
  // dipinge davvero. Ma la lentezza è la causa silenziosa numero uno
  // dell'abbandono, e non si vede in nessuna metrica di GA4.
  //
  // Qui si misura sul campo, senza librerie:
  //   LCP  quanto ci mette a comparire la cosa piu' grande della pagina
  //   CLS  quanto il contenuto salta mentre carichi
  //   INP  quanto tarda la pagina a rispondere a un tocco
  //
  // I valori vanno anche in fasce ("buono/da_migliorare/scarso") perché una
  // fascia è una DIMENSIONE (basta registrarla), mentre una media richiede
  // una metrica personalizzata. Così il dato è leggibile subito.
  //
  // RAGE CLICK: tre tocchi ravvicinati nello stesso punto. È il gesto di chi
  // preme un bottone che non risponde — l'inceppo allo stato puro.
  // ------------------------------------------------------------------
  function fascia(nome, v) {
    if (nome === 'lcp') return v <= 2500 ? 'buono' : (v <= 4000 ? 'da_migliorare' : 'scarso');
    if (nome === 'inp') return v <= 200 ? 'buono' : (v <= 500 ? 'da_migliorare' : 'scarso');
    return v <= 0.1 ? 'buono' : (v <= 0.25 ? 'da_migliorare' : 'scarso');
  }

  var vitali = {};

  /**
   * Un'etichetta breve e stabile per l'elemento colpito da un'interazione.
   *
   * Serve stabile, non precisa: le classi generate dal tema cambiano a ogni
   * build (`search-page-input--AL3krRl`), e usarle intere produrrebbe una
   * riga diversa per ogni utente invece di un conteggio. Si tiene il tag, l'id
   * e la PRIMA classe, tagliata prima del trattino doppio.
   */
  function bersaglio(n) {
    try {
      if (!n || !n.tagName) return '(rimosso)';
      var id = n.id ? '#' + n.id.split('--')[0] : '';
      var c = '';
      if (n.className && typeof n.className === 'string') {
        c = '.' + n.className.trim().split(/\s+/)[0].split('--')[0];
      }
      return (n.tagName.toLowerCase() + id + c).slice(0, 46);
    } catch (e) {
      return '(illeggibile)';
    }
  }

  /**
   * Dove se ne va il tempo di un'interazione lenta.
   *
   * Sapere CHE COSA l'utente ha toccato non basta: da agosto sappiamo che il
   * bersaglio peggiore e' `img.product-media__image`, e tre tentativi di
   * correzione (decodifica immagini, pannello accessibilita', gallerie
   * differite) non hanno mosso l'INP di un punto. Mancava il PERCHE'.
   *
   * `PerformanceEventTiming` divide l'attesa in tre pezzi, e ognuno indica
   * una cura diversa:
   *
   *   attesa        startTime → processingStart
   *                 il thread era occupato ALTROVE quando l'utente ha toccato.
   *                 Cura: togliere lavoro dal thread (script di terzi,
   *                 idratazione, cicli lunghi).
   *   elaborazione  processingStart → processingEnd
   *                 il nostro gestore d'evento e' lento. Cura: il codice del
   *                 gestore.
   *   disegno       processingEnd → fine
   *                 layout e pittura dopo il gestore. Cura: CSS, DOM enorme,
   *                 immagini non dimensionate.
   *
   * Le tre fasi si ricavano da dati che esistono anche su Safari.
   */
  function faseDominante(e) {
    try {
      var attesa = e.processingStart - e.startTime;
      var elab = e.processingEnd - e.processingStart;
      var disegno = (e.startTime + e.duration) - e.processingEnd;
      if (attesa >= elab && attesa >= disegno) return 'attesa';
      if (elab >= disegno) return 'elaborazione';
      return 'disegno';
    } catch (err) { return ''; }
  }

  /**
   * Le ultime cornici di animazione lunghe, per dare un NOME allo script che
   * ha bloccato il thread.
   *
   * ⚠️ `long-animation-frame` esiste solo sui motori Chromium. Non e' un
   * limite grave qui: il 58% delle interazioni scarse e' su Chrome e il 73%
   * su Chromium in tutto (misurato il 01/09/2026). Su Safari resta la fase,
   * che e' gia' la meta' della diagnosi.
   *
   * Si tengono poche cornici: servono solo quelle che si sovrappongono
   * all'interazione peggiore, e tenerne troppe farebbe crescere la memoria
   * su pagine lunghe.
   */
  var cornici = [];
  var corniciAttive = false;

  /**
   * ⚠️ Campo assente e campo vuoto NON sono la stessa cosa, e confonderli
   * manderebbe fuori strada. Misurato il 01/09/2026 sulla collezione
   * Sprayground: le interazioni peggiori erano a 208 ms in fase `disegno`
   * con NESSUNA cornice sovrapposta — cioe' non era codice, era il browser
   * che impagina. Se quel caso arrivasse come «vuoto», sarebbe
   * indistinguibile da «Safari non sa dirmelo», e si continuerebbe a
   * cercare lo script che non c'e'.
   *
   *   assente          il browser non espone `long-animation-frame`
   *   (nessuno script) il browser lo espone e non c'e' codice da incolpare
   *   nome:funzione    il colpevole
   */
  function scriptColpevole(e) {
    if (!corniciAttive) return '';
    try {
      var fine = e.startTime + e.duration;
      for (var i = cornici.length - 1; i >= 0; i--) {
        var f = cornici[i];
        if (f.startTime > fine || (f.startTime + f.duration) < e.startTime) continue;
        var s = (f.scripts || []).slice().sort(function (a, b) {
          return b.duration - a.duration;
        })[0];
        if (!s) continue;
        var u = String(s.sourceURL || '');
        var nome = '';
        if (u) {
          var host = '';
          try { host = new URL(u, location.href).host.replace(/^www\./, ''); } catch (x) {}
          var mio = String(location.host).replace(/^www\./, '');
          nome = (host && host !== mio ? host + '/' : '') + (senzaQuery(u) || '');
        }
        // Senza URL resta comunque l'invocatore: «l'ha chiamato un listener di
        // click su questo elemento» e' un'informazione, non rumore.
        var fn = s.sourceFunctionName || s.invoker || '';
        return ((nome || '(senza url)') + (fn ? ':' + fn : '')).slice(0, 90);
      }
    } catch (err) { /* ignora */ }
    return '(nessuno script)';
  }

  function osserva(tipo, callback) {
    try {
      var po = new PerformanceObserver(function (l) { l.getEntries().forEach(callback); });
      po.observe({ type: tipo, buffered: true });
      return po;
    } catch (e) { return null; }
  }

  try {
    osserva('largest-contentful-paint', function (e) { vitali.lcp = e.startTime; });
    osserva('layout-shift', function (e) {
      if (!e.hadRecentInput) vitali.cls = (vitali.cls || 0) + e.value;
    });
    // ⚠️ Sapere che l'INP è scarso non serve a correggerlo: serve sapere CHE
    // COSA l'utente stava toccando. Il 27/08/2026 la pagina /search risultava
    // al 26% di interazioni scarse (tre volte la media del sito) e le ipotesi
    // possibili erano quattro — ricerca predittiva, filtri, scroll della
    // griglia, apertura del pannello. Senza il bersaglio si sceglie a caso.
    //
    // `PerformanceEventTiming` porta sia il nome dell'evento (`pointerdown`,
    // `keydown`, `click`) sia il nodo colpito. Il nodo può essere già stato
    // rimosso dal DOM quando lo leggiamo: `bersaglio()` regge il null.
    // Le cornici vanno osservate PRIMA degli eventi: quando arriva
    // l'interazione peggiore, la cornice che l'ha causata deve essere gia'
    // nel buffer. `durationThreshold` tiene solo le cornici che contano.
    corniciAttive = !!osserva('long-animation-frame', function (f) {
      cornici.push(f);
      if (cornici.length > 12) cornici.shift();
    });
    osserva('event', function (e) {
      if (e.duration > (vitali.inp || 0)) {
        vitali.inp = e.duration;
        vitali.inp_dove = String(e.name || '') + ' ' + bersaglio(e.target);
        vitali.inp_fase = faseDominante(e);
        vitali.inp_script = scriptColpevole(e);
      }
    });
  } catch (e) { /* browser vecchio: si rinuncia in silenzio */ }

  // Si spedisce a pagina che se ne va: prima i valori non sono definitivi.
  var vitaliInviati = false;
  function inviaVitali() {
    if (vitaliInviati) return;
    vitaliInviati = true;
    try {
      Object.keys(vitali).forEach(function (k) {
        // `inp_dove` non è una metrica: viaggia ATTACCATA a `inp`. Se finisse
        // nel ciclo come chiave a sé, GA4 riceverebbe un quarto "vitale"
        // fantasma con un valore non numerico.
        // `inp_dove`, `inp_fase` e `inp_script` non sono metriche: viaggiano
        // ATTACCATE a `inp`. Se finissero nel ciclo come chiavi a se', GA4
        // riceverebbe altrettanti "vitali" fantasma con valori non numerici.
        if (k === 'inp_dove' || k === 'inp_fase' || k === 'inp_script') return;
        var v = k === 'cls' ? Math.round(vitali[k] * 1000) / 1000 : Math.round(vitali[k]);
        var dati = { vitale: k, valore: v, giudizio: fascia(k, vitali[k]) };
        if (k === 'inp' && vitali.inp_dove) dati.elemento = vitali.inp_dove;
        if (k === 'inp' && vitali.inp_fase) dati.fase = vitali.inp_fase;
        if (k === 'inp' && vitali.inp_script) dati.script_lento = vitali.inp_script;
        invia('vcb_web_vitals', dati);
      });
    } catch (e) { /* ignora */ }
  }
  window.addEventListener('pagehide', inviaVitali);
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'hidden') inviaVitali();
  });

  // ------------------------------------------------------------------
  // TAGLIA ESAURITA — non un guasto, un SEGNALE DI DOMANDA
  //
  // Il sito funziona: cliccare una taglia esaurita la seleziona e fa comparire
  // «Avvisami quando disponibile» (verificato sul tema live il 02/09/2026).
  // Ma lasciare la mail costa uno sforzo, cliccare no: il modulo raccoglie
  // ~296 richieste al mese, mentre i clic su taglie mancanti sono con ogni
  // probabilita' molti di piu'.
  //
  // Serve a due cose: sapere quali taglie la gente cerca e non trova (lista di
  // riacquisto), e misurare quanti di quei clic si trasformano in una mail.
  //
  // Si ascolta `change` e non `click`: il click puo' arrivare sull'etichetta,
  // sullo swatch o sul testo, mentre `change` scatta una volta sola e solo
  // quando la selezione cambia davvero.
  // ------------------------------------------------------------------
  try {
    document.addEventListener('change', function (e) {
      var t = e.target;
      if (!t || t.tagName !== 'INPUT') return;
      if (t.getAttribute('data-option-available') !== 'false') return;
      if (!t.checked) return;
      var titolo = document.querySelector('h1');
      invia('vcb_taglia_esaurita', {
        // il percorso identifica il prodotto meglio del titolo: e' stabile,
        // senza spazi e corrisponde a quello del feed
        prodotto: (location.pathname.split('/products/')[1] || location.pathname)
          .split('?')[0].slice(0, 80),
        taglia: String(t.value || '').slice(0, 24),
        variante: t.getAttribute('data-variant-id') || '',
        titolo: titolo ? titolo.textContent.trim().replace(/\s+/g, ' ').slice(0, 60) : ''
      });
    }, true);
  } catch (e) { /* ignora */ }

  // ------------------------------------------------------------------
  // CLICK MORTO — si tocca qualcosa che sembra cliccabile e non succede nulla
  //
  // E' il fratello silenzioso del rage click: quello richiede tre tocchi
  // ravvicinati, questo ne basta uno. Trova i guasti che non stiamo cercando.
  //
  // ⚠️ La difficolta' e' il FALSO POSITIVO. Il primo criterio che ho provato
  // — contare le mutazioni della PAGINA — ha subito segnato come morto il
  // click su «DETTAGLI PRODOTTO», che funziona benissimo: aprire un
  // <details> cambia UN attributo, sotto qualsiasi soglia di rumore.
  //
  // La domanda giusta non e' «la pagina e' cambiata?» ma «l'elemento che ho
  // toccato ha REAGITO?». Quindi si guarda se una mutazione ha toccato il
  // bersaglio, i suoi discendenti o i suoi antenati: una fisarmonica che si
  // apre, un menu che scende, un bottone che cambia stato passano tutti di
  // li'. Il conteggio globale resta solo come rete di sicurezza.
  // ------------------------------------------------------------------
  var RUMORE_DOM = 3;
  var ATTESA_MS = 700;
  try {
    document.addEventListener('click', function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      var interattivo = t.closest('a,button,summary,label,select,input,[role="button"],[onclick]');
      if (!interattivo) {
        // niente tag interattivo: conta solo se SEMBRA cliccabile
        try {
          if (getComputedStyle(t).cursor !== 'pointer') return;
        } catch (x) { return; }
        interattivo = t;
      }
      var urlPrima = location.href;
      var scrollPrima = window.scrollY;
      var attivoPrima = document.activeElement;
      var mutazioni = 0;
      var reagito = false;
      var mo;
      try {
        mo = new MutationObserver(function (l) {
          mutazioni += l.length;
          if (reagito) return;
          for (var i = 0; i < l.length; i++) {
            var n = l[i].target;
            if (!n) continue;
            // toccato il bersaglio, un suo discendente o un suo antenato
            if (n === interattivo) { reagito = true; return; }
            if (n.contains && n.contains(interattivo)) { reagito = true; return; }
            if (interattivo.contains && interattivo.contains(n)) { reagito = true; return; }
          }
        });
        mo.observe(document.body, { childList: true, subtree: true, attributes: true });
      } catch (x) { return; }
      setTimeout(function () {
        try {
          mo.disconnect();
          if (location.href !== urlPrima) return;                 // ha navigato
          if (Math.abs(window.scrollY - scrollPrima) > 8) return;  // ha scrollato
          if (document.activeElement !== attivoPrima) return;      // ha spostato il fuoco
          if (reagito) return;                                     // l'elemento HA reagito
          if (mutazioni > RUMORE_DOM) return;                      // la pagina e' cambiata
          invia('vcb_click_morto', {
            elemento: bersaglio(interattivo),
            testo: (interattivo.textContent || interattivo.value || '')
              .trim().replace(/\s+/g, ' ').slice(0, 40),
            mutazioni: mutazioni
          });
        } catch (x) { /* ignora */ }
      }, ATTESA_MS);
    }, true);
  } catch (e) { /* ignora */ }

  // ---- rage click ----
  try {
    var ultimi = [];
    document.addEventListener('click', function (ev) {
      var ora = Date.now();
      ultimi.push({ t: ora, x: ev.clientX, y: ev.clientY });
      ultimi = ultimi.filter(function (c) { return ora - c.t < 800; });
      if (ultimi.length < 3) return;
      var a = ultimi[0], b = ultimi[ultimi.length - 1];
      if (Math.abs(a.x - b.x) > 30 || Math.abs(a.y - b.y) > 30) return;
      ultimi = [];
      var t = ev.target || {};
      var etichetta = (t.tagName || '?') +
        (t.id ? '#' + String(t.id).slice(0, 24) : '') +
        (t.className && typeof t.className === 'string'
          ? '.' + t.className.trim().split(/\s+/)[0].slice(0, 24) : '');
      invia('vcb_rage_click', { elemento: etichetta.slice(0, 60) });
    }, true);
  } catch (e) { /* ignora */ }

})();
